import { useEffect, useRef, useState } from "react";
import { ChevronsUpDown, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandItem, CommandList } from "@/components/ui/command";
import { searchStocks } from "@vaulzo/core/api/market";

/**
 * Async ticker picker for US/LSE stocks & ETFs: results stream in from the
 * backend's /stocks/search proxy (Yahoo Finance). Search happens
 * server-side, so cmdk's own filtering is disabled. The exchange is shown
 * because the same fund lists on many venues — only USD-quoted lines are
 * accepted (validated when the quote is fetched on selection).
 */
const UNDERLINE_TRIGGER =
  "flex w-full items-center justify-between gap-2 border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-left text-[14px] text-vaulzo-ink outline-none transition-colors focus:border-vaulzo-accent";

export default function StockSelect({ value, onSelect, triggerClassName = UNDERLINE_TRIGGER }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchStocks(q);
        setResults(Array.isArray(data) ? data : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={triggerClassName}
        >
          <span className={`truncate ${value ? "" : "text-vaulzo-subtle"}`}>
            {value || "Search ticker (e.g. SPYL.L)…"}
          </span>
          <ChevronsUpDown className="h-4 w-4 flex-none text-vaulzo-fainter" strokeWidth={1.7} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[110] w-[--radix-popover-trigger-width] rounded-[14px] border-vaulzo-hairline bg-vaulzo-surface p-0 shadow-[0_10px_30px_rgba(23,24,26,0.10)]"
      >
        <Command shouldFilter={false}>
          <div className="flex items-center gap-2 border-b border-vaulzo-hairline px-3">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. CNDX or iShares NASDAQ"
              autoFocus
              className="h-11 flex-1 bg-transparent text-sm text-vaulzo-ink outline-none placeholder:text-vaulzo-subtle"
            />
            {searching && <Loader2 className="h-4 w-4 animate-spin text-vaulzo-fainter" aria-hidden />}
          </div>
          <CommandList className="max-h-[260px]">
            <CommandEmpty>
              {query.trim().length < 2 ? "Type at least 2 letters…" : searching ? "Searching…" : "No ticker found."}
            </CommandEmpty>
            {results.map((s) => (
              <CommandItem
                key={s.symbol}
                value={s.symbol}
                onSelect={() => {
                  onSelect({ symbol: s.symbol, name: s.name });
                  setQuery("");
                  setOpen(false);
                }}
                className="cursor-pointer text-[13px] leading-snug text-vaulzo-ink aria-selected:bg-vaulzo-row"
              >
                <span className="num mr-2 font-semibold">{s.symbol}</span>
                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                {s.exchange && <span className="ml-2 text-[11px] text-vaulzo-fainter">{s.exchange}</span>}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
